import React, { useEffect } from "react";
import { PlaceholderContent } from "supportbeacon/articleplaceholders";
import { invalidateContactCache, useApi } from "utilities/hookutils";
import { PreviewPanel, PreviewPanelContent, PreviewPanelFooter, PreviewPanelHeader } from "../PreviewPanel";
import { ContactPreviewPanelContent } from "./ContactPreviewPanelContent";
import { routes } from "utilities/routing/generatedRoutes";
import { ButtonPrimary } from "components/Atoms/Button";
import { useQueryClient } from "@tanstack/react-query";

interface ContactPreviewPanelProps {
  contactId: number
  onChange?: () => void
}

const ContactPreviewPanel = (props: ContactPreviewPanelProps) => {
  const { contactId } = props;

  const api = useApi(() => window.CoreApi.Contact.Preview(contactId), ["contactPreview", contactId], {
    startEnabled: true
  });

  const queryClient = useQueryClient();

  useEffect(() => {
    api.refetch();
  }, [contactId]);

  if (api.isInitialLoading) {
    return <>
      <PreviewPanelHeader title={RESX.Contact.resxContact} />
      <PreviewPanelContent>
        <PlaceholderContent />
      </PreviewPanelContent>
      <PreviewPanelFooter />
    </>;
  }

  const contact = api.data.data.contact
  const worksAt = api.data.data.worksAt;

  return <>
    <PreviewPanelHeader title={RESX.Contact.resxContact} />

    <ContactPreviewPanelContent
      contact={contact}
      worksAt={worksAt}
      onChange={async () => {
        await api.refetch();
        await invalidateContactCache(queryClient, contact.contactId);
        props.onChange?.();
      }}
    />

    <PreviewPanelFooter>
      <ButtonPrimary href={routes.contact_Detail(contactId).absolute} label={RESX.Buttons.DetailView} />
    </PreviewPanelFooter>
  </>;
};


interface ContactPreviewProps {
  trigger: JSX.Element
  onChange?: () => void
  fallbackIfNoPermission?: JSX.Element
  contactId: number
}

export const ContactPreview = (
  props: ContactPreviewProps
) => {
  if (!site.permissions.addressBook.canAccess) {
    return props.fallbackIfNoPermission || props.trigger;
  }

  return <PreviewPanel
    trigger={props.trigger}
    size="medium"
    id={`contact-preview-${props.contactId}`}>
    <ContactPreviewPanel
      onChange={props.onChange}
      contactId={props.contactId}
    />
  </PreviewPanel>;
};